import { KeyboardEvent, PointerEvent, useRef } from 'react';
import {
  CHAT_DOCK_MAX_WIDTH,
  CHAT_DOCK_MIN_WIDTH,
  resetChatDockWidth,
  setChatDockWidth,
} from '../../../lib/use-chat-dock-width';
import styles from './ChatResizeHandle.module.css';

/**
 * The grab strip on the docked chat's inline-start edge. The dock is anchored to
 * the inline-END edge, so dragging this edge toward the content widens the chat
 * (LTR: leftward; RTL: rightward — read from the element's own computed
 * direction, so it's correct under either). Drag, arrow keys (Shift = bigger
 * step), or double-click to reset. Web-only — see use-chat-dock-width.
 */
function isRtl(el: Element): boolean {
  return getComputedStyle(el).direction === 'rtl';
}

export function ChatResizeHandle({ width, label, onDraggingChange }: { width: number; label: string; onDraggingChange: (dragging: boolean) => void }) {
  const drag = useRef<{ x: number; w: number; rtl: boolean } | null>(null);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, w: width, rtl: isRtl(e.currentTarget) };
    onDraggingChange(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const s = drag.current;
    if (!s) return;
    const delta = e.clientX - s.x;
    setChatDockWidth(s.w + (s.rtl ? delta : -delta));
  };

  const end = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    onDraggingChange(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer already released
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const rtl = isRtl(e.currentTarget);
    const step = (e.shiftKey ? 48 : 16) * (rtl ? -1 : 1);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setChatDockWidth(width + step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setChatDockWidth(width - step);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      resetChatDockWidth();
    }
  };

  return (
    <div
      className={styles.handle}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(width)}
      aria-valuemin={CHAT_DOCK_MIN_WIDTH}
      aria-valuemax={CHAT_DOCK_MAX_WIDTH}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => resetChatDockWidth()}
      onKeyDown={onKeyDown}
    >
      <span className={styles.grip} aria-hidden />
    </div>
  );
}
